'use client';

import * as React from 'react';

import { cn } from '../../lib/utils';

/**
 * On/off switch, glp-frontend's page-numbers toggle as a real control: a
 * `button role="switch"`, so it is focusable and Space/Enter flip it.
 * Controlled (`checked`) or uncontrolled (`defaultChecked`).
 */
export interface SwitchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'value'> {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ className, checked, defaultChecked = false, onCheckedChange, onClick, ...props }, ref) => {
    const [internal, setInternal] = React.useState(defaultChecked);
    const on = checked ?? internal;
    return (
      <button
        ref={ref}
        type='button'
        role='switch'
        aria-checked={on}
        data-state={on ? 'checked' : 'unchecked'}
        onClick={(event) => {
          onClick?.(event);
          if (event.defaultPrevented) return;
          if (checked === undefined) setInternal(!on);
          onCheckedChange?.(!on);
        }}
        className={cn(
          'inline-flex h-7 w-14 shrink-0 cursor-pointer items-center rounded-3xl border border-slate-300 p-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-global-green/40 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50',
          on ? 'justify-end border-global-green bg-global-green' : 'justify-start bg-slate-200',
          className,
        )}
        {...props}
      >
        <span className='h-6 w-6 rounded-full bg-white shadow-sm' />
      </button>
    );
  },
);
Switch.displayName = 'Switch';

export { Switch };
